import { useState, useRef, useEffect } from "react";
import { extractTitleFromPRD } from "../utils/prdUtils";

const DEFAULT_MESSAGE = {
  id: 1,
  username: "System",
  text: "Welcome to Vamoos Studio! Create a new chat session or select an existing one to continue.",
  isSystem: true,
};

/**
 * Custom hook for handling chat messages
 * @param {string} backendUrl - The backend API URL
 * @param {string|null} currentSessionId - The current session ID
 * @param {Function} setCurrentPrdContent - Function to update PRD content
 * @param {Function} updateSessionTitle - Function to update session title
 * @returns {Object} - Chat state and handlers
 */
export const useChat = (
  backendUrl,
  currentSessionId,
  setCurrentPrdContent,
  updateSessionTitle
) => {
  // Per-session state storage using refs to persist across renders
  const sessionSendingState = useRef(new Map()); // sessionId -> boolean
  const sessionMessages = useRef(new Map()); // sessionId -> Message[]
  const sessionInputs = useRef(new Map()); // sessionId -> string

  // Track the actual current session ID (not stale from closure)
  const currentSessionRef = useRef(currentSessionId);

  // Current session state (derived from refs)
  const [messages, setMessages] = useState([DEFAULT_MESSAGE]);
  const [messageInput, setMessageInput] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Update ref when current session changes
  useEffect(() => {
    currentSessionRef.current = currentSessionId;
  }, [currentSessionId]);

  // Sync current session state when session changes
  useEffect(() => {
    if (currentSessionId) {
      // Load session state from maps
      const sessionMsgs = sessionMessages.current.get(currentSessionId) || [
        DEFAULT_MESSAGE,
      ];
      const sessionInput = sessionInputs.current.get(currentSessionId) || "";
      const sessionSending =
        sessionSendingState.current.get(currentSessionId) || false;

      setMessages(sessionMsgs);
      setMessageInput(sessionInput);
      setIsSending(sessionSending);
    } else {
      setMessages([DEFAULT_MESSAGE]);
      setMessageInput("");
      setIsSending(false);
    }
  }, [currentSessionId]);

  // Helper function to update messages for current session
  const updateSessionMessages = (updater) => {
    if (!currentSessionId) return;

    const currentMsgs = sessionMessages.current.get(currentSessionId) || [
      DEFAULT_MESSAGE,
    ];
    const newMsgs =
      typeof updater === "function" ? updater(currentMsgs) : updater;

    sessionMessages.current.set(currentSessionId, newMsgs);
    setMessages(newMsgs);
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim()) return;
    if (!currentSessionId) {
      alert("Please generate an EWA session first!");
      return;
    }

    // Capture the session ID at the start to avoid closure issues
    // when user switches sessions during async request
    const sessionId = currentSessionId;
    const userMessage = messageInput.trim();

    // Clear input for this session
    sessionInputs.current.set(sessionId, "");
    setMessageInput("");

    // Set sending state for this session only
    sessionSendingState.current.set(sessionId, true);
    setIsSending(true);

    // Add user message to this session's chat
    const userMsgId = Date.now();
    const currentMsgs = sessionMessages.current.get(sessionId) || [
      DEFAULT_MESSAGE,
    ];
    const newMsgs = [
      ...currentMsgs,
      {
        id: userMsgId,
        username: "User",
        text: userMessage,
        isUser: true,
      },
    ];
    sessionMessages.current.set(sessionId, newMsgs);
    setMessages(newMsgs);

    try {
      const response = await fetch(`${backendUrl}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage,
          silent: false,
          defer_zip: false,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const chatData = await response.json();

      // Update Product Requirements Document content if present in response
      if (chatData.prd) {
        setCurrentPrdContent(chatData.prd, sessionId);
        const title = extractTitleFromPRD(chatData.prd);
        if (title) {
          updateSessionTitle(sessionId, title);
        }
      }

      // Add assistant response to this session's chat
      if (chatData.assistant_message) {
        const msgs = sessionMessages.current.get(sessionId) || [];
        const updatedMsgs = [
          ...msgs,
          {
            id: Date.now(),
            username: "Assistant",
            text: chatData.assistant_message,
            isAssistant: true,
          },
        ];
        sessionMessages.current.set(sessionId, updatedMsgs);
        // Only update UI if still on this session (check actual current session, not closure)
        if (currentSessionRef.current === sessionId) {
          setMessages(updatedMsgs);
        }
      }

      // Add information about edits if any
      if (chatData.edits && chatData.edits.length > 0) {
        const editSummary = chatData.edits
          .map(
            (edit) =>
              `${edit.action} ${edit.path}: ${
                edit.status === "ok" ? "✅" : "❌"
              } ${edit.detail || ""}`
          )
          .join("\n");

        const msgs = sessionMessages.current.get(sessionId) || [];
        const updatedMsgs = [
          ...msgs,
          {
            id: Date.now() + 1,
            username: "System",
            text: `Applied ${chatData.edits.length} edit(s):\n${editSummary}`,
            isSystem: true,
          },
        ];
        sessionMessages.current.set(sessionId, updatedMsgs);
        // Only update UI if still on this session (check actual current session, not closure)
        if (currentSessionRef.current === sessionId) {
          setMessages(updatedMsgs);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const msgs = sessionMessages.current.get(sessionId) || [];
      const updatedMsgs = [
        ...msgs,
        {
          id: Date.now(),
          username: "System",
          text: "Failed to send message. Please try again.",
          isSystem: true,
          isError: true,
        },
      ];
      sessionMessages.current.set(sessionId, updatedMsgs);
      // Only update UI if still on this session (check actual current session, not closure)
      if (currentSessionRef.current === sessionId) {
        setMessages(updatedMsgs);
      }
    } finally {
      // Clear sending state for this session only
      sessionSendingState.current.set(sessionId, false);
      // Only update UI if still on this session (check actual current session, not closure)
      if (currentSessionRef.current === sessionId) {
        setIsSending(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handler for updating message input that also stores in session map
  const handleMessageInputChange = (value) => {
    if (currentSessionId) {
      sessionInputs.current.set(currentSessionId, value);
    }
    setMessageInput(value);
  };

  return {
    messages,
    setMessages: updateSessionMessages, // Expose for history loading
    messageInput,
    setMessageInput: handleMessageInputChange,
    isSending,
    handleSendMessage,
    handleKeyPress,
  };
};
